// Weekly Storytelling Challenges for Art of Story

import { CRISIS_HELP } from './crisisSupport.js';

const storytellingChallenges = [
  {
    id: 1,
    title: "Story Inventory Sprint",
    description: "Map 20 stories you already tell across Module 1's seven life domains: Family and Origins, Formative Experiences, Relationships, Challenges, Achievements, Passions, and Values in Action.",
    category: "Story Inventory",
    difficulty: "Beginner",
    timeCommitment: "60-90 mins",
    why: "You can't improve your narrative landscape if you don't know what stories you're already telling. This creates your baseline.",
    points: 20
  },
  {
    id: 2,
    title: "The Sensory Detail Challenge",
    description: "Add one vivid sensory detail to every story you tell this week. Track what details make the biggest impact.",
    category: "Story Impact",
    difficulty: "Beginner",
    timeCommitment: "Ongoing for 7 days",
    why: "Sensory details help listeners picture the scene and stay with you. This builds your sensory awareness muscle.",
    points: 15
  },
  {
    id: 3,
    title: "Oscillating Narrative Transformation",
    description: "Take three descending narratives ('everything went wrong') and transform them into oscillating narratives that honour both struggle and growth.",
    category: "Oscillating Narratives",
    difficulty: "Intermediate",
    timeCommitment: "2-3 hours",
    why: "Oscillating narratives name the struggle and the response. Practising them builds a new habit for telling hard stories.",
    points: 25
  },
  {
    id: 4,
    title: "5-Part Structure Mastery",
    description: "Map five of your regular stories using the 5-part structure from Module 4: Setup, Catalyst, Tension, Climax, Resolution. Practise telling each one.",
    category: "Story Structure",
    difficulty: "Intermediate",
    timeCommitment: "3-4 hours over the week",
    why: "Structure transforms rambling anecdotes into compelling narratives. This embeds the pattern in your storytelling muscle memory.",
    points: 20
  },
  {
    id: 5,
    title: "Emotional Arc Mapping",
    description: "Chart the emotional journey of three stories from start to finish. Identify flat sections and add variation where needed.",
    category: "Emotional Arc",
    difficulty: "Intermediate",
    timeCommitment: "90 mins",
    why: "Flat emotional arcs bore listeners. Variation creates engagement. This trains you to see and shape emotional pacing.",
    points: 20
  },
  {
    id: 6,
    title: "Vulnerability Calibration Practice",
    description: "Share vulnerable stories in three different contexts (professional, social, intimate) and notice what level feels appropriate for each.",
    category: "Vulnerability",
    difficulty: "Advanced",
    timeCommitment: "3 conversations",
    why: "The 10% Rule is this course's own rule of thumb, not a research finding, so real conversations are the test. This builds your calibration skills across different contexts.",
    points: 30
  },
  {
    id: 7,
    title: "Story Rehearsal Week",
    description: "Record yourself telling three stories. Watch/listen back. Assess filler words, pacing, pauses, volume, and confidence. Re-record with improvements.",
    category: "Delivery",
    difficulty: "Intermediate",
    timeCommitment: "2-3 hours",
    why: "You can't improve delivery without feedback. Recording provides objective data about patterns you can't see whilst speaking.",
    points: 20
  },
  {
    id: 8,
    title: "The Pure Listening Challenge",
    description: "Have five conversations where you only ask questions and listen. Share nothing about yourself unless directly asked.",
    category: "Story Listening",
    difficulty: "Intermediate",
    timeCommitment: "5 conversations",
    why: "It's easy to redirect to your own stories too quickly. This isolates empathic listening as a distinct skill.",
    points: 20
  },
  {
    id: 9,
    title: "Story Type Diversification",
    description: "Identify which of Module 1's six story types you're missing (origin, turning point, high point, low point, redemption, contamination) and deliberately craft one of each.",
    category: "Story Inventory",
    difficulty: "Intermediate",
    timeCommitment: "2-3 hours",
    why: "A rich narrative landscape includes different story types. This fills gaps in your repertoire.",
    points: 20
  },
  {
    id: 10,
    title: "The Specificity Sprint",
    description: "Replace every vague phrase ('one day,' 'somewhere,' 'someone') with specific details in all stories you tell for one week.",
    category: "Story Impact",
    difficulty: "Beginner",
    timeCommitment: "7 days",
    why: "Specific stories are easier to picture and remember than generic ones.",
    points: 15
  },
  {
    id: 11,
    title: "Family Oscillating Narrative Project",
    description: `Interview a family member about a difficult time in family history. Help them tell it as an oscillating narrative (struggle + growth). Go gently, and stop if it becomes too painful for either of you. If you, or the person you're listening to, might be in danger or thinking about suicide, get help now: ${CRISIS_HELP}.`,
    category: "Oscillating Narratives",
    difficulty: "Advanced",
    timeCommitment: "2-3 hours",
    why: "Marshall Duke considers the oscillating pattern the healthiest for family stories, though it hasn't been tested as a cause of resilience. You're adding to your family's store of shared stories.",
    points: 30
  },
  {
    id: 12,
    title: "Context Adaptation Challenge",
    description: "Take one story and create four versions adapted for: professional, social, intimate, and public speaking contexts. Practise each version.",
    category: "Story Structure",
    difficulty: "Advanced",
    timeCommitment: "3-4 hours",
    why: "The same story should land differently in different contexts. This builds your adaptation muscle for appropriate storytelling.",
    points: 25
  },
  {
    id: 13,
    title: "Abstract to Concrete Translation",
    description: "Identify 10 abstract emotion labels in your stories ('I was sad,' 'I felt happy') and replace them with sensory, physical descriptions.",
    category: "Emotional Arc",
    difficulty: "Intermediate",
    timeCommitment: "2 hours",
    why: "Lisa Feldman Barrett's theory: the brain constructs emotions from sensations and context. Show feelings, don't just name them.",
    points: 20
  },
  {
    id: 14,
    title: "The Shame-to-Growth Reframe",
    description: `Identify three stories framed with shame ('I am broken') and reframe them with growth ('I learned something hard'). If this stirs up more than a reframe can hold, pause and talk to someone you trust. If you might be in danger or thinking about suicide, get help now: ${CRISIS_HELP}.`,
    category: "Vulnerability",
    difficulty: "Advanced",
    timeCommitment: "2-3 hours",
    why: "As Brené Brown puts it, shame says 'I am bad'; guilt says 'I did something bad.' In this course's view, framing a hard story around what you did and learnt, not who you are, changes how you carry it.",
    points: 25
  },
  {
    id: 15,
    title: "Filler Word Awareness Week",
    description: "Track every 'um,' 'like,' and 'you know' for one day. Then spend the week trying a short pause instead.",
    category: "Delivery",
    difficulty: "Intermediate",
    timeCommitment: "7 days",
    why: "A steady stream of fillers can distract listeners; pauses give your words room. This week builds awareness and replacement.",
    points: 20
  },
  {
    id: 16,
    title: "No Redirecting Challenge",
    description: "For one week, when someone shares a story, let them finish completely before sharing yours. No 'That reminds me of...' interruptions.",
    category: "Story Listening",
    difficulty: "Beginner",
    timeCommitment: "7 days",
    why: "Redirecting to your own story is an easy habit to slip into. This builds your muscle for staying with someone else's narrative before jumping to your own.",
    points: 15
  },
  {
    id: 17,
    title: "Pattern Recognition Project",
    description: "Analyse 15 stories you tell regularly. What patterns emerge? Are they mostly professional? Mostly descending? What's missing?",
    category: "Story Inventory",
    difficulty: "Beginner",
    timeCommitment: "90 mins",
    why: "Your story landscape shows how you tend to see yourself. Pattern recognition shows you which narratives you lean on most.",
    points: 15
  },
  {
    id: 18,
    title: "Memory Hook Installation",
    description: "Add surprise, concrete imagery, or meaningful repetition to five stories. Test them on different people and track what they remember.",
    category: "Story Impact",
    difficulty: "Intermediate",
    timeCommitment: "2-3 hours + testing",
    why: "Surprise, specificity and concrete details create memory hooks. This builds those craft habits into your storytelling practice.",
    points: 25
  },
  {
    id: 19,
    title: "The 'Do You Know?' Family Challenge",
    description: "Complete the 20-question 'Do You Know?' Family Narrative Scale in the Toolkit. Identify gaps in your family story knowledge and interview relatives to fill them.",
    category: "Oscillating Narratives",
    difficulty: "Intermediate",
    timeCommitment: "3-5 hours",
    why: "Duke, Lazarus and Fivush (2008) found that children who knew more family history tended to have higher self-esteem, a stronger sense of control, better family functioning and lower anxiety. These are correlations. Gathering these stories connects you to what Duke and Fivush call your intergenerational self.",
    points: 25
  },
  {
    id: 20,
    title: "The 2-Minute Edit Challenge",
    description: "Take three stories that run long and edit them ruthlessly to under 2 minutes each. Time yourself and cut until they fit.",
    category: "Story Structure",
    difficulty: "Intermediate",
    timeCommitment: "2-3 hours",
    why: "Cutting a story down shows you what its core really is. This builds your editing muscle for punchy, tight delivery.",
    points: 20
  },
  {
    id: 21,
    title: "Dialogue Integration Sprint",
    description: "Add one line of direct dialogue to every story you tell this week. Let characters speak instead of summarising.",
    category: "Emotional Arc",
    difficulty: "Beginner",
    timeCommitment: "7 days",
    why: "Dialogue brings stories to life and reveals character. This trains you to hear and include the voices in your memories.",
    points: 15
  },
  {
    id: 22,
    title: "The 10% Rule Practice",
    description: "In five conversations across different contexts, try this course's 10% rule of thumb: share a little beyond your current level of closeness. Track responses.",
    category: "Vulnerability",
    difficulty: "Advanced",
    timeCommitment: "5 conversations",
    why: "Well-judged vulnerability can deepen relationships. Too much too soon can strain trust; too little keeps things shallow. This calibrates the middle path.",
    points: 30
  },
  {
    id: 23,
    title: "Presence and Pacing Practice",
    description: "Record yourself telling three stories. Assess volume variation, pacing shifts, and strategic emphasis. Re-record with conscious modulation.",
    category: "Delivery",
    difficulty: "Intermediate",
    timeCommitment: "2-3 hours",
    why: "Monotone delivery kills even the best material. This builds your awareness of vocal variety and presence.",
    points: 20
  },
  {
    id: 24,
    title: "Empathic Receiving Challenge",
    description: `When five people share stories this week, practise pure receiving: no fixing, no minimising, no redirecting. Just 'That sounds really hard.' Listening comes first, but if someone may be unsafe or isn't coping, help them connect with support. If you, or the person you're listening to, might be in danger or thinking about suicide, get help now: ${CRISIS_HELP}.`,
    category: "Story Listening",
    difficulty: "Intermediate",
    timeCommitment: "5 conversations",
    why: "Carl Rogers' condition of empathic understanding: grasping the other person's internal frame of reference. This isolates receiving as a skill.",
    points: 20
  }
];

// Get challenge of the week based on the week number
export const getChallengeOfTheWeek = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = now - start;
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  const weekOfYear = Math.floor(diff / oneWeek);
  const challengeIndex = weekOfYear % storytellingChallenges.length;
  return storytellingChallenges[challengeIndex];
};

// Get a random challenge
export const getRandomChallenge = () => {
  return storytellingChallenges[Math.floor(Math.random() * storytellingChallenges.length)];
};

// Get challenges by difficulty
export const getChallengesByDifficulty = (difficulty) => {
  return storytellingChallenges.filter(challenge => challenge.difficulty === difficulty);
};

// Get challenges by category
export const getChallengesByCategory = (category) => {
  return storytellingChallenges.filter(challenge => challenge.category === category);
};

// Get all categories
export const getChallengeCategories = () => {
  return [...new Set(storytellingChallenges.map(challenge => challenge.category))];
};

export default storytellingChallenges;
